import { t } from '@transifex/native';
import { T, useT } from '@transifex/react';
import {
  Link,
  Form,
  useActionData,
  useLoaderData,
  useSearchParams,
  useNavigation,
} from 'react-router';
import { Button, buttonVariants } from '~/components/ui/button';
import { useForm, getFormProps, getInputProps } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';
import { z } from 'zod';
import { emailSchema } from '~/lib/validation';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { usePasswordVisibility } from '~/lib/usePasswordVisibility';
import { PasswordVisibilityButton } from '~/components/ui/password-visibility-icon';
import { FormGroup } from '~/components/ui/form-ui';
import { FormErrors } from '~/components/ui/form-error';
import { ampli } from '~/third-party/ampli/.client';
import { cn } from '~/lib/utils';
import { $path } from 'safe-routes';
import type { Route } from '../../routes/login/+types/login';

export const loginSchema = z.object({
  email: z.string().email(t('Please enter a valid email address')),
  password: z.string({ required_error: t('Please enter your password') }),
});

/** Dispatch component — picks the appropriate step based on the URL */
function Login() {
  const loaderData = useLoaderData<Route.ComponentProps['loaderData']>();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');
  const stepParam = searchParams.get('step');
  const action = next ? `/login?next=${encodeURIComponent(next)}` : '/login';

  const email = loaderData?.ssoLoginEmail ?? '';

  if (stepParam === 'password' && email) {
    return <LoginPasswordStep action={action} email={email} />;
  }

  return <LoginEmailStep action={action} defaultEmail={email} />;
}

function LoginEmailStep({ action, defaultEmail }: { action: string; defaultEmail: string }) {
  const t = useT();
  const actionData = useActionData();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const [form, fields] = useForm({
    onValidate: function ({ formData }: { formData: FormData }) {
      return parseWithZod(formData, { schema: emailSchema });
    },
    shouldValidate: 'onInput',
    shouldRevalidate: 'onInput',
    lastResult: actionData,
  });

  return (
    <>
      <Form
        className="flex w-full flex-col gap-6"
        {...getFormProps(form)}
        noValidate
        method="post"
        action={action}
      >
        <input type="hidden" name="intent" value="discover" />
        <FormGroup>
          <Label htmlFor={fields.email.id}>
            <T _str="Email" />
          </Label>
          <Input
            {...getInputProps(fields.email, { type: 'email' })}
            placeholder={t('Your email address')}
            defaultValue={defaultEmail}
          />
          <FormErrors errors={fields.email.errors} />
        </FormGroup>
        <FormErrors errors={form.errors} />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <T _str="Checking..." /> : <T _str="Continue" />}
        </Button>
      </Form>
      <SignUpLink />
    </>
  );
}

function LoginPasswordStep({ action, email }: { action: string; email: string }) {
  const t = useT();
  const actionData = useActionData();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';
  // Include step=password so the URL persists after a non-redirect (error) response,
  // keeping the loader aware we're on the password step.
  const formAction = action.includes('?') ? `${action}&step=password` : `${action}?step=password`;
  const [showPassword, togglePasswordVisibility] = usePasswordVisibility();

  const [form, fields] = useForm({
    onValidate: function ({ formData }: { formData: FormData }) {
      return parseWithZod(formData, { schema: loginSchema });
    },
    onSubmit: function () {
      ampli.clickLogIn();
    },
    shouldValidate: 'onInput',
    shouldRevalidate: 'onInput',
    lastResult: actionData,
  });

  return (
    <>
      <Form
        className="flex w-full flex-col gap-6"
        {...getFormProps(form)}
        noValidate
        method="post"
        action={formAction}
      >
        <input type="hidden" name="intent" value="login" />
        <input type="hidden" name="email" value={email} />
        <FormGroup>
          <Label>
            <T _str="Email" />
          </Label>
          <div className="text-text-600 flex items-center gap-2 text-body-big">
            <span>{email}</span>
            <Link to={action} className="text-green-500 underline">
              <T _str="Change" />
            </Link>
          </div>
        </FormGroup>
        <FormGroup>
          <Label htmlFor={fields.password.id}>
            <T _str="Password" />
          </Label>
          <div className="relative flex items-center">
            <Input
              {...getInputProps(fields.password, { type: showPassword ? 'text' : 'password' })}
              placeholder={t('Your password')}
              className="pr-10"
            />
            <PasswordVisibilityButton show={showPassword} onClick={togglePasswordVisibility} />
          </div>
          <FormErrors errors={fields.password.errors} />
        </FormGroup>
        <Link to={$path('/forgot-password')} className="text-body-big text-green-500 underline">
          <T _str="Forgot Password?" />
        </Link>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <T _str="Logging in..." /> : <T _str="Log in" />}
        </Button>
      </Form>
      <SignUpLink />
    </>
  );
}

function SignUpLink() {
  return (
    <Link
      className={cn(
        buttonVariants({ variant: 'underlined' }),
        'wrap-break-word whitespace-pre-wrap'
      )}
      to="/signup"
    >
      <T _str="Don't have an account yet? Sign Up" />
    </Link>
  );
}

export { Login, LoginEmailStep, LoginPasswordStep };
